import { useCallback, useState } from 'react'

import { SplitsDialogContent } from '.'
import { usePasskeyConfirmation } from '~/hooks/usePasskeyConfirmation'
import { captureException } from '~/utils/errors'

export const PasskeyConfirmationDialog = ({
  id,
  title,
  confirmText,
  confirmDisabled,
  confirmDisabledReason,
  children,
  onConfirmed,
  onClose,
}: {
  id: string
  title: string
  confirmText: string
  confirmDisabled?: boolean
  confirmDisabledReason?: string
  children?: React.ReactNode
  onConfirmed: () => Promise<void>
  onClose?: () => void
}) => {
  const { confirmWithPasskey, isConfirming } = usePasskeyConfirmation()
  const [isRunning, setIsRunning] = useState(false)

  const handleConfirm = useCallback(async () => {
    if (!(await confirmWithPasskey())) return

    setIsRunning(true)
    try {
      await onConfirmed()
      onClose?.()
    } catch (error) {
      captureException(error)
    } finally {
      setIsRunning(false)
    }
  }, [confirmWithPasskey, onConfirmed, onClose])

  return (
    <SplitsDialogContent
      id={id}
      title={title}
      variant="confirmation"
      isLoading={isConfirming || isRunning}
      confirmButton={{
        text: confirmText,
        onClick: handleConfirm,
        isDisabled: confirmDisabled,
        disabledReason: confirmDisabledReason,
      }}
    >
      {children ?? <p>You will need to confirm with your passkey to proceed</p>}
    </SplitsDialogContent>
  )
}
